import React, {useState, useRef, Suspense, useEffect} from 'react';
import Map from './pendant-map-assets/Map.jsx';
import LocationList from './pendant-map-assets/LocationList.jsx';
import Navigation from "./Navigation.jsx";
import {ToastContainer} from "react-toastify";
import ApiRequester from './ApiRequester.jsx';
import {ReactSession} from "../utils/ReactSession.jsx";
import {AES, enc} from "crypto-js";
import {iAmHigherAuthority, showModal} from "../libs.jsx";

const PalPendantMap = () => {

    let dData, bData;
    if (ReactSession.get("PAL") != null){
        bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
        dData = JSON.parse(bData.toString(enc.Utf8));
    }

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [campusId, setCampusId] = useState(dData?.acnt);
    const [locations, setLocations] = useState([]);
    const [alertedDevices, setAlertedDevices] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [count, setCount] = useState(0);
    const mapRef = useRef(null);

    useEffect(() => {
        let interval = null;
        interval = setInterval(() => {
             setCount(count => count - 1);
        }, 1000);
        
        if (count <= 0) {
            ApiRequester({reqEndpoint: "myfetch", pKey: "campus_id", pVal: dData?.acnt,
                                payload: {campus_id: campusId}, procedure: "getallpendantlocations",
                                reqType: "stored"}).then(data => {
                if (data && data.length > 0){
                    setLocations(data[0]?.locations ? data[0].locations : []);
                    setAlertedDevices(data[0]?.alerted ? data[0].alerted : []);
                }
                setLoaded(true);
            })
            setCount(() => 6);
        }
        
        return () => clearInterval(interval);
    }, [count]);

    const appContainerStyle = {
        width: '100vw',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
    };

    const mapWrapperStyle = {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    };

    const listWrapperStyle = {
        width: '20%',
        minWidth: "200px",
        height: '100%',
        background: '#0081C7',
        position: 'fixed',
        top: 0,
        zIndex: 1000,
        overflow: 'hidden',
    };

    const handleLocationSelect = (location) => {
        setSelectedLocation(location);
        if (mapRef.current) {
            mapRef.current.flyTo({
                center: [location.longitude, location.latitude],
                zoom: 15,
                duration: 2000
            });
        }
    };

    return (
        <>
            <Navigation />
            <div className={"main-content"}>
                <div style={appContainerStyle}>
                    <div style={listWrapperStyle}>
                        <LocationList onLocationSelect={handleLocationSelect} onLocationsUpdate={setLocations}
                                      onAlertedDevicesUpdate={setAlertedDevices} locations={locations}
                                      alertedDevices={alertedDevices} mapRef={mapRef} campusId={campusId}
                                      setCampusId={(item) => setCampusId(item)}
                                      setCount={(item) => setCount(item)}/>
                    </div>
                    <div style={mapWrapperStyle}>
                        <Map locations={locations} alertedDevices={alertedDevices} ref={mapRef}/>
                    </div>
                </div>
            </div>
            <Suspense fallback={"Loading..."}>
                <ToastContainer position={"bottom-right"} theme={"colored"} newestOnTop={true} pauseOnHover={true}
                                autoClose={10000} closeOnClick={true}/>
            </Suspense>
            <button
                type={"button"}
                className={"help-corner-button"}
                onClick={() => showModal("helpmodal")}
                title={"Page Help Popup"}
                aria-label={"Open Help"}
            >
                <i className={"fe fe-help-circle help-corner-button-icon"}></i>
            </button>
            <div className={"modal fade help-modal-pop"} id={"helpmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticHelpBackdropLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog modal-lg"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title"} id={"staticHelpBackdropLabel"}>Pendant Map Page Help</h2>
                            <button type={"button"} className={"btn-close"} data-bs-dismiss={"modal"} aria-label={"Close"}/>
                        </div>
                        <div className={"modal-body"}>
                            <div className={"mb-4"}>
                                <h4 className={"text-primary mb-3"}>
                                    <i className={"fe fe-info me-2"}></i>Getting Started
                                </h4>
                                <p className={"mb-2"}>
                                    This page displays a real-time map view of all pendant device locations in your community. The map shows where each resident's pendant is located, allowing you to track and monitor device positions visually.
                                </p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-layout me-2 text-primary"}></i>Page Layout
                                </h5>
                                <p className={"mb-2"}>The page consists of two main sections:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Location List (Left Sidebar)</strong>: A scrollable list of all residents with pendant devices, displayed in a blue sidebar on the left side of the screen.</li>
                                    <li className={"mb-2"}><strong>Interactive Map (Main Area)</strong>: A full-screen map showing the geographic locations of all pendant devices as markers.</li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-search me-2 text-primary"}></i>Searching Locations
                                </h5>
                                <p className={"mb-2"}>Use the <strong>search bar</strong> at the top of the location list to quickly find residents by name or pendant number. The search filters the list in real-time as you type, making it easy to locate specific residents.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-map me-2 text-primary"}></i>Viewing Locations on the Map
                                </h5>
                                <p className={"mb-2"}>Click on any resident in the location list to:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>Fly to that location on the map with a smooth animation</li>
                                    <li className={"mb-2"}>Zoom in to a detailed view of the location</li>
                                    <li className={"mb-2"}>Highlight the corresponding marker on the map</li>
                                </ul>
                                <p className={"mb-2"}>You can also click directly on markers on the map to view detailed information about that resident's pendant.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-info me-2 text-primary"}></i>Marker Information
                                </h5>
                                <p className={"mb-2"}>Click on any marker on the map to see a popup with:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Resident Name</strong>: The name of the resident assigned to the pendant</li>
                                    <li className={"mb-2"}><strong>Pendant Number</strong>: The unique device identifier (manufacture UUID)</li>
                                    <li className={"mb-2"}><strong>Last Ping</strong>: The timestamp of the last location update from the device</li>
                                    <li className={"mb-2"}><strong>Alert Status</strong>: If the device is in an alert state, this will be prominently displayed</li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-flag me-2 text-primary"}></i>Marker Colors
                                </h5>
                                <p className={"mb-2"}>Markers on the map are color-coded to indicate device status:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Blue Markers</strong>: Normal devices with no active alerts</li>
                                    <li className={"mb-2"}><strong>Red Markers</strong>: Devices that are currently in an alert state (larger and red colored)</li>
                                </ul>
                                <p className={"mb-2"}>In the location list, alerted devices are also highlighted with red text and an exclamation mark (!).</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-radio me-2 text-primary"}></i>Pinging Devices
                                </h5>
                                <p className={"mb-2"}>Each location in the list has a <strong>"Ping"</strong> button. Clicking this button sends a location request to the pendant device, which will respond with its current location. This is useful for:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>Getting an immediate location update</li>
                                    <li className={"mb-2"}>Verifying device connectivity</li>
                                    <li className={"mb-2"}>Testing device responsiveness</li>
                                </ul>
                                <p className={"mb-2"}>You'll receive a success or error notification when the ping is completed.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-target me-2 text-primary"}></i>Tracking Mode
                                </h5>
                                <p className={"mb-2"}>Enable <strong>Tracking Mode</strong> using the toggle switch at the bottom of the location list. When enabled:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>Press and hold (or long-press) on any resident in the list for 1 second to start tracking that device</li>
                                    <li className={"mb-2"}>Tracked devices will be highlighted in green in the list</li>
                                    <li className={"mb-2"}>The tracking counter shows how many devices you're currently tracking</li>
                                    <li className={"mb-2"}>The map will automatically fly to a tracked device when its location changes</li>
                                    <li className={"mb-2"}>Press and hold again on a tracked device to stop tracking it</li>
                                </ul>
                                <p className={"mb-2"}>Tracking mode is useful for monitoring specific residents' movements in real-time.</p>
                            </div>

                            {iAmHigherAuthority(30, dData.role_weight)
                                ? <div className={"mb-4"}>
                                    <h5 className={"fw-bold mb-2"}>
                                        <i className={"fe fe-home me-2 text-primary"}></i>Campus Selection
                                    </h5>
                                    <p className={"mb-2"}>If you have higher authority permissions, you'll see a campus dropdown at the top of the location list. Use this to switch between different campuses and view their pendant locations.</p>
                                </div>
                                : <></>
                            }

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-refresh-cw me-2 text-primary"}></i>Auto-Refresh
                                </h5>
                                <p className={"mb-2"}>The map automatically refreshes every 6 seconds to fetch the latest location data from all pendant devices. This ensures you're always viewing current information without needing to manually refresh the page.</p>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-navigation me-2 text-primary"}></i>Map Navigation
                                </h5>
                                <p className={"mb-2"}>You can interact with the map using standard map controls:</p>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}><strong>Zoom</strong>: Use the mouse wheel, pinch gestures, or zoom controls to zoom in and out</li>
                                    <li className={"mb-2"}><strong>Pan</strong>: Click and drag to move around the map</li>
                                    <li className={"mb-2"}><strong>Click Markers</strong>: Click on any marker to view detailed information</li>
                                </ul>
                            </div>

                            <div className={"mb-3"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-settings me-2 text-primary"}></i>Additional Tips
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>The location list is scrollable if you have many residents - use the scrollbar or mouse wheel to navigate through the list.</li>
                                    <li className={"mb-2"}>Alerted devices are prioritized visually with red markers and text, making them easy to spot.</li>
                                    <li className={"mb-2"}>When tracking multiple devices, the map will automatically follow the first device that moves.</li>
                                    <li className={"mb-2"}>The map starts with a view of the entire United States, but will zoom in when you select a location.</li>
                                    <li className={"mb-2"}>If a device hasn't reported its location recently, it may not appear on the map.</li>
                                    <li className={"mb-2"}>Use the search function to quickly find specific residents when you have a large number of devices.</li>
                                </ul>
                            </div>
                        </div>
                        <div className={"modal-footer"}>
                            <button type={"button"} className={"btn btn-secondary"} data-bs-dismiss={"modal"}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PalPendantMap;